import { useState } from "react";
import { toast } from "sonner";
import { Reveal } from "./Reveal";
import { sendContact } from "../lib/api";
import { MODEL } from "../data";

const Field = ({ label, children }) => (
  <label className="block">
    <span className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
      {label}
    </span>
    {children}
  </label>
);

export const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", subject: "", message: "" });
  const [loading, setLoading] = useState(false);

  const onChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      toast.error("Please fill in your name, email and message.");
      return;
    }
    setLoading(true);
    try {
      await sendContact(form);
      toast.success("Message sent — thank you. We'll be in touch shortly.");
      setForm({ name: "", email: "", subject: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const inputCls =
    "mt-3 w-full bg-transparent border-b border-border py-3 text-foreground text-lg font-light placeholder:text-muted-foreground/50 focus:outline-none focus:border-accent transition-colors";

  return (
    <section id="contact" data-testid="contact-section" className="px-6 lg:px-12 py-24 lg:py-40">
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
        <div className="lg:col-span-5">
          <Reveal>
            <h2 className="mt-6 font-serif text-5xl lg:text-7xl font-light leading-[0.9] tracking-tight text-foreground uppercase">
              Let&apos;s
              <br />
              <span className="italic">work</span>
              <br />
              together.
            </h2>
            <div className="mt-12 space-y-6">
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.2em] text-muted-foreground">
                  Instagram
                </p>
                <a
                  href="https://instagram.com/bbbhinderr"
                  target="_blank"
                  rel="noreferrer"
                  data-testid="instagram-handle"
                  className="mt-2 inline-block font-serif text-xl font-light text-foreground hover:text-accent transition-colors"
                >
                  {MODEL.instagram}
                </a>
              </div>
            </div>
          </Reveal>
        </div>

        <div className="lg:col-span-6 lg:col-start-7">
          <Reveal delay={0.1}>
            <form onSubmit={onSubmit} data-testid="contact-form" className="space-y-8">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-8">
                <Field label="Full Name">
                  <input
                    name="name"
                    value={form.name}
                    onChange={onChange}
                    placeholder="Your name"
                    data-testid="contact-name"
                    className={inputCls}
                  />
                </Field>
                <Field label="Email">
                  <input
                    name="email"
                    type="email"
                    value={form.email}
                    onChange={onChange}
                    placeholder="you@studio.com"
                    data-testid="contact-email"
                    className={inputCls}
                  />
                </Field>
              </div>
              <Field label="Subject">
                <input
                  name="subject"
                  value={form.subject}
                  onChange={onChange}
                  placeholder="Campaign / Editorial / Runway"
                  data-testid="contact-subject"
                  className={inputCls}
                />
              </Field>
              <Field label="Message">
                <textarea
                  name="message"
                  value={form.message}
                  onChange={onChange}
                  rows={4}
                  placeholder="Tell us about the project…"
                  data-testid="contact-message"
                  className={`${inputCls} resize-none`}
                />
              </Field>

              <button
                type="submit"
                disabled={loading}
                data-testid="contact-submit"
                className="group relative inline-flex items-center gap-4 border border-foreground px-10 py-4 text-xs font-bold uppercase tracking-[0.25em] text-foreground overflow-hidden transition-colors duration-500 hover:text-background disabled:opacity-50"
              >
                <span className="relative z-10">
                  {loading ? "Sending…" : "Send Inquiry"}
                </span>
                <span className="relative z-10 inline-block h-px w-8 bg-current transition-all duration-500 group-hover:w-12" />
                <span className="absolute inset-0 z-0 bg-foreground -translate-x-full transition-transform duration-500 ease-[cubic-bezier(0.16,1,0.3,1)] group-hover:translate-x-0" />
              </button>
            </form>
          </Reveal>
        </div>
      </div>
    </section>
  );
};
